/**
 * Session state: the account, the selected viewer profile, and the auth flows.
 *
 * The profile lives here rather than in the token because viewers switch
 * profiles far more often than they sign in, and re-minting a token per switch
 * would be a poor trade for a TV client.
 */
import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import type { ReactNode } from "react";
import { api, ApiError, tokens } from "@/api/client";
import type {
  Account,
  AuthResponse,
  CaptchaFields,
  Profile,
  RegisterResponse,
  VerifyEmailResponse,
} from "@/api/types";

/**
 * What registration produced.
 *
 * Signing up no longer necessarily signs you in. With the verification gate on,
 * the account exists and carries no credentials until the address is confirmed,
 * so the form has to branch rather than navigate.
 */
export interface SignUpOutcome {
  /** True when the account exists but is waiting on a confirmation click. */
  verificationRequired: boolean;
  /** False when the account was created and the email could not be sent. */
  emailSent: boolean;
  email: string;
}

interface SessionValue {
  account: Account | null;
  profile: Profile | null;
  loading: boolean;
  /**
   * `captcha` carries the honeypot and the signed challenge token. It is
   * optional only so a deployment with the captcha switched off, and the unit
   * tests, need not synthesise one; the server is the authority on whether it
   * is required.
   */
  signIn: (email: string, password: string, captcha?: CaptchaFields) => Promise<void>;
  signUp: (
    email: string,
    password: string,
    displayName: string,
    captcha?: CaptchaFields,
  ) => Promise<SignUpOutcome>;
  /**
   * Spend a confirmation link. Returns whether this was the first, genuine
   * confirmation - only that one carries credentials, so only that one signs
   * the person in.
   */
  confirmEmail: (token: string) => Promise<{ alreadyVerified: boolean }>;
  signOut: () => void;
  selectProfile: (profile: Profile) => void;
  refreshAccount: () => Promise<void>;
}

const SessionContext = createContext<SessionValue | null>(null);

export function SessionProvider({ children }: { children: ReactNode }) {
  const [account, setAccount] = useState<Account | null>(null);
  const [profile, setProfile] = useState<Profile | null>(null);
  const [loading, setLoading] = useState(true);

  const loadAccount = useCallback(async () => {
    if (!tokens.access()) {
      setAccount(null);
      setProfile(null);
      setLoading(false);
      return;
    }
    try {
      const me = await api.get<Account>("/api/v1/auth/me");
      setAccount(me);
      const storedId = tokens.profile();
      const resolved = me.profiles.find((item) => item.id === storedId) ?? null;
      setProfile(resolved);
      if (resolved) tokens.setProfile(resolved.id);
    } catch (error) {
      if (error instanceof ApiError && error.isAuthError) tokens.clear();
      setAccount(null);
      setProfile(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void loadAccount();
  }, [loadAccount]);

  const applyAuth = useCallback((payload: AuthResponse) => {
    tokens.set(payload.access_token, payload.refresh_token);
    setAccount(payload.user);
    const chosen = payload.profile ?? payload.user.profiles[0] ?? null;
    if (chosen) {
      tokens.setProfile(chosen.id);
      setProfile(chosen);
    }
  }, []);

  const value = useMemo<SessionValue>(
    () => ({
      account,
      profile,
      loading,
      async signIn(email, password, captcha) {
        applyAuth(
          await api.anonymousPost<AuthResponse>("/api/v1/auth/login", {
            email,
            password,
            ...captcha,
          }),
        );
      },
      async signUp(email, password, displayName, captcha) {
        const created = await api.anonymousPost<RegisterResponse>("/api/v1/auth/register", {
          email,
          password,
          display_name: displayName,
          plan_code: "standard",
          ...captcha,
        });
        // A 201 with no tokens is the hard gate doing its job, not a failure.
        // The caller shows "check your email"; there is nothing to apply.
        if (created.verification_required || !created.access_token) {
          return {
            verificationRequired: true,
            emailSent: created.email_sent !== false,
            email: created.user.email,
          };
        }
        applyAuth(created as AuthResponse);
        return { verificationRequired: false, emailSent: true, email: created.user.email };
      },
      async confirmEmail(token) {
        const result = await api.anonymousPost<VerifyEmailResponse>("/api/v1/auth/verify-email", {
          token,
        });
        // A repeat click - somebody clicking twice, a mail client prefetching
        // the link - confirms nothing new and mints nothing. Signing them in
        // from it would hand a session to whoever holds a spent link.
        if (result.already_verified || !result.access_token) {
          return { alreadyVerified: true };
        }
        applyAuth(result as AuthResponse);
        return { alreadyVerified: false };
      },
      signOut() {
        tokens.clear();
        setAccount(null);
        setProfile(null);
      },
      selectProfile(next) {
        tokens.setProfile(next.id);
        setProfile(next);
      },
      refreshAccount: loadAccount,
    }),
    [account, profile, loading, applyAuth, loadAccount],
  );

  return <SessionContext.Provider value={value}>{children}</SessionContext.Provider>;
}

export function useSession(): SessionValue {
  const value = useContext(SessionContext);
  if (!value) throw new Error("useSession must be used inside a SessionProvider");
  return value;
}
